import React from 'react';
import { Heading, Text } from '@restworld/ui-ds-base';
import { SkeletonTextLoading } from '../skeleton-loading';
import { cn } from '@restworld/utils-common';

export type PresentationCardProps = {
  isError?: boolean;
  icon: React.ReactNode;
  label: string | React.ReactNode;
  header: string | React.ReactNode;
  variant?: 'start' | 'center';
  hide?: boolean;
  contentStyling?: string;
  loading?: boolean;
  headerStyling?: string;
  maxChars?: number;
};

export const PresentationCard = ({
  isError = false,
  icon,
  label,
  header,
  variant = 'start',
  hide = false,
  contentStyling,
  loading,
  headerStyling,
  maxChars,
}: PresentationCardProps) => {
  return (
    <Wrapper {...{ variant, hide }}>
      <IconContainer {...{ isError, variant }}>{icon}</IconContainer>
      <ContentContainer {...{ contentStyling }}>
        {loading ? (
          <SkeletonTextLoading width="small" />
        ) : typeof label !== 'string' ? (
          label
        ) : (
          <Text
            weight={variant === 'center' ? 'normal' : 'medium'}
            color={variant === 'center' ? 'default' : 'black'}
            size={2}
          >
            {label}
          </Text>
        )}
        {loading ? (
          <SkeletonTextLoading width="medium" className="mt-2" />
        ) : typeof header !== 'string' ? (
          header
        ) : (
          <Heading
            as="span"
            size={5}
            weight={variant === 'center' ? 'semibold' : 'normal'}
            color={isError ? 'error' : variant === 'center' ? 'default' : 'muted'}
            className={headerStyling}
            maxChars={maxChars}
          >
            {header}
          </Heading>
        )}
      </ContentContainer>
    </Wrapper>
  );
};

const Wrapper = ({
  variant,
  hide,
  className,
  children,
  ...props
}: Pick<PresentationCardProps, 'variant' | 'hide'> & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-row gap-2 items-start justify-start max-w-full w-full',
      variant === 'center' && 'items-center',
      hide && 'hidden',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

const IconContainer = ({
  isError,
  variant,
  className,
  children,
  ...props
}: Pick<PresentationCardProps, 'isError' | 'variant'> & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center w-10 min-w-[40px] h-10 bg-white rounded-lg text-primary-700',
      variant === 'center' && 'bg-primary-50',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

export const ContentContainer = ({
  contentStyling,
  className,
  children,
  ...props
}: Pick<PresentationCardProps, 'contentStyling'> & React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-1 flex-col items-start', contentStyling, className)} {...props}>
    {children}
  </div>
);
